import { useEffect, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { Box, Typography, useMediaQuery } from '@mui/material';
import {
  AssessmentButton,
  ChapterCard,
  ChipBadge,
  DashboardGrid,
  IStyles,
  Loader,
  NoContentCard,
  SubjectCard,
  deserify,
  getLocalStorage,
  getMediaBasePath,
  pxToRem,
  pxTovW,
  theme,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import { ProfileRolesEnum } from '@protos/user_management/ums.db_pb';
import {
  ASSESMENT_LIBRARY,
  HOME,
  HOMEWORK_MANAGE,
  HOMEWORK_TOPIC_SELECTION,
} from '../../../../routeHandling/RoutesNomenclature';
import { interactionEvent } from '../../../Auth/auth.events';

import { AssessmentEnum } from '@protos/learning_management/lms.db_pb';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from '../../../../reduxStore/reduxHooks';
import { useGlobalContext } from '../../../../app/Context/GlobalContextProvider';
import {
  resetCreatedAssessmentStatus,
  resetFetchedAssessmentDetails,
  resetSelectedChapter,
  resetSelectedTasksInfo,
  resetSelectedTopic,
  setAssessmentData,
  setSubjectWiseChaptersInfo,
} from '../../reducer/assessmentFlow.slice';
import { useDownloadContext } from '../../../../app/Context/DownloadContextProviderV2';
import { getRoundedDownValue } from '../../../../utils/functions';
import { ModuleSchoolContentStatsData } from '@protos/learning_management/lms.common.apis_pb';

const styles: IStyles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    boxSizing: 'border-box',
    padding: {
      md: `${pxTovW(50)} ${pxTovW(40)}`,
      lg: `${pxTovW(50)} ${pxTovW(240)}`,
    },
    gap: { xs: pxToRem(20), md: pxTovW(40) },
  },
  subjectBox: {
    width: '100%',
    display: 'flex',
    gap: { xs: pxToRem(15), md: 0 },
    boxSizing: 'border-box',
    flexDirection: { xs: 'column', md: 'row' },
    justifyContent: 'space-between',
    paddingX: { xs: pxToRem(20), md: 0 },
    mt: { xs: pxToRem(20), md: 0 },
  },
  onGoing: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxTovW(10) },
    mt: { md: pxTovW(15) },
  },
  allChaptersContainer: {
    width: '100%',
    boxSizing: 'border-box',

    '& .grid': {
      padding: { xs: pxToRem(12), md: pxTovW(12) },
    },
    '& .grid > div': {
      borderBottom: '1px solid #B8B8B8',
    },
    '& .grid:not(:nth-child(3n))': {
      borderRight: { xs: '1px solid none', md: '1px solid #B8B8B8' },
      borderImage:
        'linear-gradient(to bottom, white 10%,#B8B8B8 10% 90%,white 90% 100%) 1',
    },
  },

  lowerBox: {
    display: 'flex',
    flexDirection: 'column',
    gap: { xs: pxToRem(15), md: pxTovW(25) },
  },

  textWithBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    padding: { xs: pxToRem(20), md: 0 },
  },
};

export default function HwChapterSelection() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { subject_id } = useParams();
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const { isOffline } = useDownloadContext();
  const { setSelectedFunction } = useGlobalContext();
  const teacher_id = getLocalStorage('userId');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isIpad = useMediaQuery(theme.breakpoints.down('lg'));
  const isIpadOnly = !isMobile && isIpad;

  const { subjectwise_chapters_info } = deserify(
    useAppSelector((state) => state.assessment)
  );
  const { assesment_flow_data, assesment_creation_type } = deserify(
    useAppSelector((state) => state.assessment)
  );
  const {
    ContentCommonAPIServiceV2ClientWithStatusCodeHandler,
    LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler,
    LmsCommonAPIServiceV1ClientWithStatusCodeHandler,
  } = useCommonServiceClientContext();
  const [loading, setLoading] = useState(false);
  // getting current class and subject selected
  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  // getting image path from user_info.subjects.iconUrl, by matching className,sectionName,subjectName
  const { user_info } = deserify(useAppSelector((state) => state.auth));
  const [schoolContentStats, setSchoolContentStats] = useState<
    ModuleSchoolContentStatsData | undefined
  >();

  const findSubjectImage = () => {
    let subjectImage = '';

    user_info?.teachClassSubjects.forEach((classData) => {
      if (
        class_subject_info?.classname === classData.className &&
        class_subject_info?.section === classData.sectionName
      ) {
        classData.subjects.forEach((subjectData) => {
          if (class_subject_info?.subject === subjectData.subjectName) {
            subjectImage = getMediaBasePath(
              subjectData.iconUrl,
              'schoolnetStaticAssetBucket'
            );
          }
        });
      }
    });

    return subjectImage;
  };
  async function fetchSubjectChapterInfo(subjectId: string) {
    try {
      setLoading(true);
      const response =
        await ContentCommonAPIServiceV2ClientWithStatusCodeHandler.fetchSubjectChapterInfoV2(
          {
            personId: BigInt(teacher_id),
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,

            subjectId: Number(subjectId),
            bookId: class_subject_info?.bookId,
            isAssessFlow: true,
          }
        );

      if (response?.data) {
        const data = response.data;
        // console.log(typeof data, data);
        dispatch(setSubjectWiseChaptersInfo(data));
      } else {
        // setError(new Error('Login failed'));
      }

      setLoading(false);
    } catch (err) {
      setLoading(false);
      // setError(err);
      console.log(err);
    }
  }

  const fetchAssessmentsByModule = async (subjectId: number) => {
    try {
      setLoading(true);
      const response =
        await LmsAssessmentCommonAPIServiceV1ClientWithStatusCodeHandler.fetchAssessmentsByModule(
          {
            personId: teacher_id,
            modeOfConduction: assesment_creation_type?.modeOfConduction,
            personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
            subjectId: subjectId,
            assessmentType: AssessmentEnum.ASSESSMENT_HOMEWORK,
          }
        );
      if (response?.data) {
        dispatch(setAssessmentData(response.data));
      }
      setLoading(false);
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(resetSelectedTasksInfo());
    dispatch(resetCreatedAssessmentStatus());
    dispatch(resetFetchedAssessmentDetails());
    dispatch(resetSelectedChapter());
    dispatch(resetSelectedTopic());
    if (subject_id) {
      fetchSubjectChapterInfo(subject_id);
      // fetchAssessmentsByModule(Number(subject_id));
    }
  }, [subject_id]);
  const [searchParams, setSearchParams] = useSearchParams();
  const home = searchParams.get('home');
  useEffect(() => {
    const onBackClick = () => {
      if (home) {
        navigate(HOME);
      } else {
        navigate(HOMEWORK_MANAGE);
      }
    };
    setSelectedFunction(() => onBackClick);
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  const newItems =
    subjectwise_chapters_info?.chapters &&
    subjectwise_chapters_info.chapters.length > 0
      ? subjectwise_chapters_info?.chapters
          .sort((a, b) => a.chapterNo - b.chapterNo)
          .map((elem, index) => (
            <ChapterCard
              key={index}
              withArrow
              variant="normal"
              image={getMediaBasePath(
                elem.posterImagesUrl,
                'processedMediaBucket'
              )}
              mainHeading={elem.chapterNo + '. ' + elem.chapterTitle}
              blueSubText={`${elem.noOfTopics} topics`}
              rootStyle={{ width: '100%' }}
              cardClickHandler={async () => {
                navigate(
                  `${HOMEWORK_TOPIC_SELECTION}/${subject_id}/${elem.chapterId}`
                );
                await interactionEvent({
                  context: 'Choose_chapter',
                  name: 'CHAPTER_OPEN',
                  pathSegments: pathSegments,
                  isOffline: isOffline,
                });
              }}
              itemDetails={{
                iconName: 'questions',
                text: `${elem.chapterStats?.questionCount ?? 0} Questions`,
              }}
            />
          ))
      : [];

  // const selectedChapterId = subjectwise_chapters_info?.response.find(
  //   (val) => !!val.noOfTopics
  // )?.chapterId;
  // const ongoingChapter = subjectwise_chapters_info?.response.find(
  //   (value) => value.chapterId === selectedChapterId
  // );
  // const ongoingChapterId = subjectwise_chapters_info?.ongoingChapterId;
  // const ongoingChapter = subjectwise_chapters_info?.chapters.find(
  //   (val) => val.chapterId === ongoingChapterId
  // );

  // const getContentStats = async () => {
  //   try {
  //     const response =
  //       await LmsCommonAPIServiceV1ClientWithStatusCodeHandler.getModuleSchoolContentStats(
  //         {
  //           personId: teacher_id,

  //           personType: ProfileRolesEnum.PROFILE_ROLE_TEACHER,
  //           subjectId: class_subject_info?.subjectId,
  //         }
  //       );
  //     if (response.code === 200) {
  //       setSchoolContentStats(response.data);
  //     }
  //   } catch (error) {
  //     console.log('error:', error);
  //   }
  // };

  // // -----------------------------------------------------------

  // useEffect(() => {
  //   getContentStats();
  // }, [class_subject_info]);

  // -----------------------------------------------------------

  return (
    <Box sx={styles.root}>
      <Box sx={styles.subjectBox}>
        <SubjectCard
          image={findSubjectImage()}
          title={class_subject_info ? class_subject_info?.subject : ''}
          subTitle={
            class_subject_info
              ? 'Class ' +
                class_subject_info?.classname +
                class_subject_info?.section
              : ''
          }
          headingData={{
            ...(subjectwise_chapters_info?.subjectStats?.questionCount && {
              yellowData: {
                value: getRoundedDownValue(
                  subjectwise_chapters_info?.subjectStats?.questionCount
                ),
                text: 'Questions',
              },
            }),
            ...(subjectwise_chapters_info?.subjectStats
              ?.learningOutcomeCount && {
              blueData: {
                value: getRoundedDownValue(
                  subjectwise_chapters_info?.subjectStats?.learningOutcomeCount
                ),
                text: 'Learning Outcomes',
              },
            }),
          }}
          rootStyle={{ width: { xs: '90vw', md: 'max-content' } }}
        />

        {/* {ongoingChapter && (
          <Box sx={styles.onGoing}>
            {isMobile && <Typography variant="h2">Ongoing Chapter</Typography>}
            <ChapterInfoCard
              variant={isIpadOnly ? 'large' : 'medium'}
              tagName="Ongoing"
              withArrow
              image={getMediaBasePath(
                ongoingChapter?.posterImagesUrl,
                'processedMediaBucket'
              )}
              mainHeading={
                ongoingChapter?.chapterNo +
                  '. ' +
                  ongoingChapter?.chapterTitle || '_'
              }
              rootStyle={{
                // backgroundColor: 'red',
                width: { xs: '100%', md: pxToRem(360), lg: pxTovW(692) },
                cursor: 'pointer',
              }}
              blueSubText={`${ongoingChapter?.noOfTopics} Topics`}
              cardClickHandler={async () => {
                await interactionEvent({
                  url: 'Teacher_hw_chapter_selection',
                  context: 'Ongoing_chapter',
                  name: 'CHAPTER_OPEN',
                });
                navigate(
                  `${HOMEWORK_TOPIC_SELECTION}/${subject_id}/${ongoingChapter?.chapterId}`
                );
              }}
            />
          </Box>
        )} */}
        <Box sx={{ marginY: { md: 'auto' } }}>
          <AssessmentButton
            backgroundColor="#CBEECD"
            imageName="library-group"
            variant="regular"
            clickHandler={async () => {
              navigate(`${ASSESMENT_LIBRARY}`);
              await interactionEvent({
                context: 'Navigation_bar',
                name: 'CHOOSE_FROM_LIBRARY',
                pathSegments: pathSegments,
                isOffline: isOffline,
              });
            }}
            heading={{ bold: 'Choose', regular: 'From Library' }}
            headingData={[
              ...(subjectwise_chapters_info?.subjectStats?.assessmentCount
                ? [
                    {
                      value: getRoundedDownValue(
                        subjectwise_chapters_info?.subjectStats?.assessmentCount
                      ),
                      text: 'Ready Assessments',
                    },
                  ]
                : []),
            ]}
          />
        </Box>
      </Box>

      <Box sx={styles.lowerBox}>
        <Box sx={styles.textWithBadge}>
          <Typography variant="h2">
            Choose Chapter to Create Homework
          </Typography>
          <Typography variant={isIpadOnly ? 'h3' : 'h4'}>
            <ChipBadge
              label={
                (subjectwise_chapters_info?.chapters &&
                  subjectwise_chapters_info?.chapters.length) ||
                0
              }
              color="primary"
              size="small"
            />
          </Typography>
        </Box>

        <Box sx={{ marginBottom: { xs: pxToRem(20), md: 0 } }}>
          {/* <DashboardGrid items={items} WebNoOfCols={3} mobileNoOfCols={1} /> */}

          {loading === true ? (
            <Loader />
          ) : newItems ? (
            <DashboardGrid
              items={[...newItems]}
              WebNoOfCols={3}
              mobileNoOfCols={1}
            />
          ) : (
            [
              <NoContentCard
                variant="info"
                icon="cards"
                text="No Chapters to show"
              />,
            ]
          )}
        </Box>
      </Box>
    </Box>
  );
}
